import type { IDetectMetricResult } from '~/modules/analytics/types';
import type { IGetTextPrice } from '~/modules/shopify';

import { numberWithCommas, numberWithCommasLean } from './report';
import { IMetricFormatter } from '~/modules/analytics/types';
import { getTimeDurationLabel } from './metricTime';
import { calcPercentageString } from '~/utils';
import { formatMs } from '~/presentation/ui';

export const detectDataV2By = <T, R = IDetectMetricResult>({
  data,
  formatter,
  extractValue,
}: {
  data: T | undefined;
  formatter: IMetricFormatter;
  extractValue: (value: T | undefined) => number | undefined;
}): R => {
  const valueEmpty = {
    formatter,
    value: 0,
  };
  if (!data) return valueEmpty as R;

  const value = extractValue(data);
  if (typeof value === 'undefined') return valueEmpty as R;

  return {
    value,
    formatter,
  } as R;
};

export const formatMetricByFormatter = ({
  value,
  formatter,
  getTextPrice,
  compact,
}: {
  value: IDetectMetricResult['value'];
  formatter: IDetectMetricResult['formatter'];
  getTextPrice: IGetTextPrice;
  compact?: boolean;
}): string => {
  const dataValue = value ?? 0;

  switch (formatter) {
    case IMetricFormatter.PERCENTAGE_STRING: {
      if (typeof dataValue !== 'number') return calcPercentageString(0, 1, 2) ?? '';
      return calcPercentageString(dataValue, 1, 2) ?? '';
    }
    case IMetricFormatter.NUMBER_COMMAS:
    case IMetricFormatter.NUMBER_COMMAS_LEAN:
    case IMetricFormatter.NORMAL: {
      if (compact && typeof dataValue === 'number' && Math.abs(dataValue) >= 1_000) {
        return new Intl.NumberFormat('en', {
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(dataValue);
      }
      const fn =
        formatter === IMetricFormatter.NUMBER_COMMAS_LEAN ? numberWithCommasLean : numberWithCommas;
      return fn(dataValue.toString());
    }
    case IMetricFormatter.DURATION: {
      return getTimeDurationLabel(Number(dataValue));
    }
    case IMetricFormatter.DURATION_MS: {
      return getTimeDurationLabel(Number(dataValue) / 1000);
    }
    case IMetricFormatter.MILLISECONDS: {
      return formatMs(Number(dataValue));
    }
    case IMetricFormatter.CURRENCY: {
      return getTextPrice(dataValue, false, compact ? { compact } : undefined);
    }
    default:
      return `${dataValue}`;
  }
};
